import { useState, FC, Dispatch, SetStateAction, KeyboardEvent, MutableRefObject, useRef } from 'react'
import { OptionsType } from 'services/types'
import isSearching from './helpers/isSearching'
import { useScroll } from './hooks'

import { t } from 'translations'

type ScrollableListProps = {
  options: OptionsType[]
  selectedOptions: string[]
  keyCursor: number | null
  setKeyCursor: Dispatch<SetStateAction<number | null>>
  onClickOrEnter: (option: OptionsType) => void
  listRef: MutableRefObject<HTMLUListElement | null>
}

const ScrollableList: FC<ScrollableListProps> = ({ options, selectedOptions, keyCursor, setKeyCursor, onClickOrEnter, listRef }) => {
  const [mouseCursor, setMouseCursor] = useState<number | null>(null)

  const listItemsRefById = useRef<Record<string, HTMLLIElement | null>>({})
  // Handle the scrolling in the list
  useScroll(keyCursor, listRef, listItemsRefById, options)

  // Handle key down in the list
  const handleKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    event.preventDefault()
    const key = event.key
    if (options.length) {
      // Intercept the ctrl/cmd + Enter key combinaison to ignore it (taken into account at the window level in FilterBar)
      if (isSearching(event)) return

      switch (key) {
        case 'Enter':
          onClickOrEnter(options[keyCursor || 0])
          break
        case 'ArrowDown':
          setKeyCursor(prevState => {
            if (prevState === null) return 0
            return prevState < options.length - 1 ? prevState + 1 : prevState
          })
          break
        case 'ArrowUp':
          setKeyCursor(prevState => (prevState !== null && prevState > 0 ? prevState - 1 : prevState))
          break
        default:
          return
      }
    }
  }

  return (
    <ul
      className="mt-2 pl-0 border-top"
      style={{ maxHeight: '200px', overflowY: 'auto', outline: 'none' }}
      ref={listRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onMouseLeave={() => setMouseCursor(null)}
    >
      {options.length === 0 ? (
        <li className={`list-item-merinio d-flex justify-content-between align-items-baseline mt-2 ml-4`}>
          <div>
            <i className="far fa-empty-set mr-2"></i> {t('searchBar.NO_RESULTS')}
          </div>
        </li>
      ) : (
        options.map((option, index) => {
          const selected = selectedOptions.includes(option._id)
          const active = index === keyCursor || index === mouseCursor
          const activeAndSelected = selected && active
          const activeOnly = !selected && active
          const selectedOnly = selected && !active
          return (
            <li
              key={option._id}
              className={`list-item-merinio ${
                selectedOnly ? 'selected' : activeOnly ? 'active' : activeAndSelected ? 'active-and-selected' : ''
              } d-flex justify-content-between align-items-baseline ${index !== 0 ? 'border-top' : ''} ${index === options.length - 1 ? 'border-bottom' : ''}`}
              onClick={() => onClickOrEnter(option)}
              onMouseEnter={() => setMouseCursor(index)}
              onMouseLeave={() => setMouseCursor(null)}
              ref={ref => (listItemsRefById.current[option._id] = ref)}
            >
              <div className="d-flex text-break justify-content-start align-items-baseline">
                <i className={`far fa-check mr-2 ${selected ? '' : 'invisible'}`}></i>
                <span className={`d-flex flex-column`}>
                  {option.label}
                  <small className={`${selected ? '' : 'text-muted'}`}>{option.subLabel}</small>
                </span>
              </div>
              <i className={`far fa-times ${selected ? '' : 'invisible'}`}></i>
            </li>
          )
        })
      )}
    </ul>
  )
}
export default ScrollableList
